import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Link, Stack, Tab, Tabs, Tooltip, Typography } from "@mui/material";

import { ErrorState, Loading } from "@lopie/kernel-ui";
import { api } from "../lib/projects-api.ts";
import Board from "./Board.jsx";
import Roadmap from "./Roadmap.jsx";
import ProjectSettings from "../components/ProjectSettings.jsx";

/** One project: its board, and a roadmap of its epics over time — start date,
 *  target date, a Gantt, and each epic's rolled-up tasks, the way the tasks
 *  monolith this was ported from lays it out. People, epics and tags are
 *  loaded once here and shared with the tabs and the task drawer. Whether the
 *  caller may edit — a viewer on a private project may not — is derived from
 *  the project's members and threaded down so a read-only board hides the
 *  controls it would refuse anyway. */
export default function ProjectView({ project, onBack, me, initialTaskId, onJumpConsumed }) {
  const [tab, setTab] = useState(0);
  const [board, setBoard] = useState(null);
  const [people, setPeople] = useState([]);
  const [epics, setEpics] = useState([]);
  const [tags, setTags] = useState([]);
  const [settings, setSettings] = useState(false);
  const [failure, setFailure] = useState(null);

  const loadBoard = useCallback(() => {
    setFailure(null);
    return api.board(project.id).then(setBoard).catch(setFailure);
  }, [project.id]);
  const loadEpics = useCallback(
    () => api.epics(project.id).then((r) => setEpics(r.items)).catch(() => {}), [project.id]);
  const loadTags = useCallback(
    () => api.tags(project.id).then((r) => setTags(r.items)).catch(() => setTags([])), [project.id]);

  useEffect(() => {
    loadBoard(); loadEpics(); loadTags();
    api.people().then((r) => setPeople(r.items)).catch(() => setPeople([]));
  }, [loadBoard, loadEpics, loadTags]);

  const current = board?.project || project;
  const readOnly = useMemo(() => {
    const members = current.members || [];
    return members.length > 0 && !members.some((m) => m.id === me && m.role === "member");
  }, [current, me]);

  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Link component="button" onClick={onBack} underline="hover">← Projects</Link>
        <Box sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: current.color || "primary.main" }} />
        <Typography variant="h5">{current.name}</Typography>
        <Chip size="small" variant="outlined" label={current.key} sx={{ fontFamily: "monospace" }} />
        {current.archived ? <Chip size="small" label="archived" /> : null}
        {current.onHold ? <Chip size="small" color="warning" variant="outlined" label="on hold" /> : null}
        {current.maintenanceMode ? <Chip size="small" color="info" variant="outlined" label="maintenance" /> : null}
        {current.internal ? <Chip size="small" variant="outlined" label="internal" /> : null}
        {readOnly ? (
          <Tooltip title="You are a viewer on this project"><Chip size="small" color="info" variant="outlined" label="read-only" /></Tooltip>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {!readOnly ? <Button size="small" onClick={() => setSettings(true)}>Settings</Button> : null}
      </Stack>

      <Tabs value={tab} onChange={(_, next) => setTab(next)}>
        <Tab label="Board" />
        <Tab label="Roadmap" />
      </Tabs>

      {failure ? <ErrorState failure={failure} onRetry={loadBoard} /> : null}

      {!failure && tab === 0 ? (
        board ? (
          <Board project={current} board={board} people={people} epics={epics}
            tags={tags} me={me} readOnly={readOnly} onReload={loadBoard}
            initialOpenTaskId={initialTaskId} onOpenedInitial={onJumpConsumed} />
        ) : <Loading label="Reading the board" />
      ) : null}

      {!failure && tab === 1 ? (
        <Roadmap project={current} epics={epics} tasks={board?.tasks || []} people={people}
          readOnly={readOnly} onReload={() => { loadEpics(); loadBoard(); }} />
      ) : null}

      {settings ? (
        <ProjectSettings project={current} people={people} tags={tags} me={me}
          onClose={() => setSettings(false)}
          onChanged={() => { loadBoard(); loadTags(); }}
          onDeleted={() => { setSettings(false); onBack(); }} />
      ) : null}
    </Stack>
  );
}
